<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      {{ createNew ? 'New' : 'Edit' }} Tag Category
    </template>
    <template #body>
      <SubSection :sub-section-heading="createNew ? '' : 'Edit'" :sub-section-nav="subSectionNav">
        <template #body>
          <VeeForm  v-if="selectedTagCategory" @submit="onSubmit">
            <TagCategoryDisabled :category="selectedTagCategory"/>
              <div class="row">
                <TextInput v-model="selectedTagCategory.category_name"
                  name="category_name"
                  label="Tag Category Name"
                  rules="required"
                  col-style="form-group-standard-column" />
              </div>
              <div class="row">
                <TextInput v-model="selectedTagCategory.category_identifier"
                  name="category_identifier"
                  label="Tag Category Identifier"
                  rules="required"
                  maxlength="3"
                  placeholder="Max 3 characters."
                  col-style="form-group-standard-column" />
              </div>
              <h3 class="mt-4">Report Display Options</h3>

              <div class="row">
                <SelectInput v-model="selectedTagCategory.internal_reporting_display"
                  name="internal_reporting_display"
                  label="Internal Reporting"
                  rules="required"
                  :options="reportingDisplayOptions"
                  label-key="label"
                  value-key="value"
                  col-style="form-group-standard-column" />
                <SelectInput v-model="selectedTagCategory.external_reporting_display"
                  name="external_reporting_display"
                  label="External Reporting"
                  rules="required"
                  :options="reportingDisplayOptions"
                  label-key="label"
                  value-key="value"
                  col-style="form-group-standard-column" />
              </div>

              <div class="row" v-if="!createNew">
                <TextInput v-model="selectedTagCategory.created_by.full_name"
                  name="created_by"
                  label="Created By"
                  :disabled="true"
                  col-style="form-group-standard-column" />
                <TextInput v-model="selectedTagCategory.updated_by.full_name"
                  name="last_updated_by"
                  label="Last updated by"
                  :disabled="true"
                  col-style="form-group-standard-column" />
              </div>


            <!-- save controls -->
            <ActionToolbar :component-state="currentState" 
              :secondary-enabled="true"
              :success-message="successMessage"
              :tertiary-enabled="!createNew"
              primary-button-text="Save"
              :secondary-button-text="createNew ? 'Cancel' : 'Close'"
              :error-message="formError"
              :tertiary-button-text="selectedTagCategory.disabled_at ? 'Enable' : 'Disable' "
              :toolbar-size="createNew ? 'col-12' : 'col-md-6'"
              @secondary-click="cancel()"
              @tertiary-click="toggle()">
            </ActionToolbar>
          </VeeForm>
        </template>
      </SubSection>
    </template>
  </CardSection>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTagSubSectionNav } from '@/src/components/admin-area/match-tags/useTagSubSectionNav';
// @ts-ignore
import CardSection from "@/src/components/shared/CardSection.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import SubSection from "@/src/components/shared/SubSection.vue";
import { Form as VeeForm } from 'vee-validate';
// @ts-ignore
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
// @ts-ignore
import SelectInput from "@/src/components/shared/FormComponents/SelectInput.vue";
// @ts-ignore
import { useTagStore } from '@/src/stores/tag';
// @ts-ignore
import TagCategoryDisabled from "@/src/components/admin-area/match-tags/_tagCategoryDisabled.vue";
import { TagCategory } from "@/src/models/match/tag-category";
import { useSavableState } from '@/src/composables/useSavableState';

const route = useRoute();
const router = useRouter();
const tagStore = useTagStore();

const {
  majorError,
  formError,
  currentState,
  isSavableStateLoading,
  setSavableStateLoading,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateSuccess,
  handleMajorError,
  handleFormError,
} = useSavableState();

const { subSectionNav } = useTagSubSectionNav();

// Component state
const selectedTagCategory = ref<any>(null);
const successMessage = ref<string | null>(null);
const reportingDisplayOptions = ref<{ label: string; value: string }[]>([]);

const createNew = computed(() => !route.params.id);

function loadTagCategoryForm(tagAPIResponse: any) {
  selectedTagCategory.value = tagAPIResponse ? TagCategory.buildFromAPI(tagAPIResponse) : new TagCategory();
}

async function onSubmit() {
  try {
    setSavableStateProcessing();
    const method = createNew.value ? tagStore.createTagCategory : tagStore.updateTagCategory;
    const payload = selectedTagCategory.value.toAPIPayload();
    const response: any = await method(payload);

    if (createNew.value) router.push({ name: 'admin_tag_categories_edit', params: { id: response.data.id } });
    loadTagCategoryForm(response.data);

    successMessage.value = 'Successfully saved!';
    setSavableStateSuccess();
  } catch(error) {
    handleFormError(error);
  }
}

function cancel() {
  router.push({ name: 'admin_tag_categories' });
}

async function toggle() {
  let response;
  try {
    setSavableStateProcessing();
    if (selectedTagCategory.value.disabled_at) {
      if (!confirm(`Are you sure you want to enable the tag category (${selectedTagCategory.value.category_name})?`)) {
        setSavableStateIdle();
        return;
      }
      response = await tagStore.enableTagCategory(selectedTagCategory.value.id);
      successMessage.value = 'Successfully enabled tag category!';
    } else {
      if (!confirm(`Are you sure you want to disable the tag category (${selectedTagCategory.value.category_name})?`)) {
        setSavableStateIdle();
        return;
      }
      response = await tagStore.disableTagCategory(selectedTagCategory.value.id);
      successMessage.value = 'Successfully disabled tag category!';
    }
    loadTagCategoryForm(response.data);
    setSavableStateSuccess();
  } catch(error) {
    handleFormError(error);
  }
}

onMounted(async () => {
  try {
    setSavableStateLoading();
    const [optionsResponse, tagCategoryResponse] = await Promise.all([
      tagStore.loadReportingDisplayOptions(),
      route.params.id ? tagStore.loadTagCategory(route.params.id) : Promise.resolve(null)
    ]);
    reportingDisplayOptions.value = optionsResponse?.data ?? [];
    loadTagCategoryForm(tagCategoryResponse?.data ?? null);
    setSavableStateIdle();
  } catch(error) {
    handleMajorError(error);
  }
});
</script>
